import NavBar from '../components/navbar';
import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Spinner, Modal } from 'react-bootstrap';
import { signup, login, setCurrentUser } from '../services/authService';
import { createSubscription } from '../services/billingService';
import styles from './signupStyles'; // You can create a CSS/JS style file for custom styles
import Button3D from './Button3D';
import { ACCOUNT_TYPE_SEATS, FEATURE_FLAGS, TRIAL_CONFIG, isTrialFeatureEnabled, BASE_PLAN_PRICING, PHONE_ADDON, ANNUAL_DISCOUNT } from '../constants';
import { fetchBillingConfig } from '../services/billingConfigService';
import { createAndSubmitPaymentMethodFromElements } from '../services/stripeService';
import { useStripe, useElements, CardElement } from '@stripe/react-stripe-js';
import { useAuth } from '../context/AuthContext';
import TeamCollaborationExplainer from '../components/TeamCollaborationExplainer';
import TeamNotificationTooltip from '../components/TeamNotificationTooltip';
import { FaEye, FaEyeSlash } from 'react-icons/fa';

function Signup({ onProfileClick, onBillingClick, onSeatsUpdate }) {
  console.log('🚀 Signup component mounting...');
  console.log('Props received:', { onProfileClick, onBillingClick, onSeatsUpdate });
  
  const navigate = useNavigate();
  const location = useLocation();
  const { login: authLogin } = useAuth(); // Use auth context for proper state management
  console.log('✅ Hooks initialized successfully');

  // Handle sign in click from navbar
  const handleSignInClick = () => {
    navigate('/', { state: { openLogin: true } });
  };

  // Always start at step 1
  console.log('🔄 Initializing state...');
  const [step, setStep] = useState(1);
  const [showConfirmation, setShowConfirmation] = useState(() => {
    return sessionStorage.getItem('signup_showConfirmation') === 'true';
  });
  const [selectedType, setSelectedType] = useState(null);
  console.log('✅ Initial state initialized');
  
  // Billing configuration state
  const [billingConfig, setBillingConfig] = useState(null);
  const [configLoading, setConfigLoading] = useState(true);
  console.log('💰 Billing config state:', { billingConfig: !!billingConfig, configLoading });
  
  const [showTeamModal, setShowTeamModal] = useState(false);
  const [showBrokerageModal, setShowBrokerageModal] = useState(false);
  const [userDetails, setUserDetails] = useState(() => {
    const saved = sessionStorage.getItem('signup_userDetails');
    return saved ? JSON.parse(saved) : { email: '', password: '', name: '', last_name: '', company: '' };
  });
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [touched, setTouched] = useState({ name: false, last_name: false, email: false, password: false, confirmPassword: false, terms: false });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPasswordField, setShowConfirmPasswordField] = useState(false);
  const [phoneSlots, setPhoneSlots] = useState(0);
  const [skipPhoneAddon, setSkipPhoneAddon] = useState(false);
  const [billingCycle, setBillingCycle] = useState('monthly');
  const [userDetailsError, setUserDetailsError] = useState('');
  
  // Trial-related state
  const [isTrialMode, setIsTrialMode] = useState(false);
  const [, setSkipPaymentMethod] = useState(false);
  
  // Confirmation checkbox state
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  // On mount, check sessionStorage for confirmation state and restore data
  useEffect(() => {
    if (sessionStorage.getItem('signup_showConfirmation') === 'true') {
      setShowConfirmation(true);
      // Restore selectedType and userDetails if present
      const savedType = sessionStorage.getItem('signup_selectedType');
      if (savedType) setSelectedType(JSON.parse(savedType));
      const savedUser = sessionStorage.getItem('signup_userDetails');
      if (savedUser) setUserDetails(JSON.parse(savedUser));
    }
  }, []);

  // Handle URL parameters for pre-selecting trial plan
  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const planParam = urlParams.get('plan');
    
    if (planParam === 'trial') {
      setIsTrialMode(true);
      // Pre-select the trial plan (set to 'trial' string to match plan.key)
      setSelectedType('trial');
      // Save to sessionStorage to persist selection
      sessionStorage.setItem('signup_selectedType', JSON.stringify('trial'));
    } else {
      // For fresh signups (no URL parameters), clear any previous selection
      // unless we're in a confirmation flow
      if (sessionStorage.getItem('signup_showConfirmation') !== 'true') {
        sessionStorage.removeItem('signup_selectedType');
        setSelectedType(null);
      }
    }
  }, [location.search]);

  // Load billing configuration on component mount
  useEffect(() => {
    console.log('🔄 Starting billing config load useEffect...');
    const loadBillingConfig = async () => {
      try {
        console.log('📡 Fetching billing configuration...');
        setConfigLoading(true);
        const config = await fetchBillingConfig();
        console.log('✅ Billing config loaded successfully:', !!config);
        setBillingConfig(config);
      } catch (error) {
        console.error('❌ Failed to load billing configuration:', error);
        // Continue with fallback pricing if API fails
      } finally {
        console.log('🏁 Setting configLoading to false');
        setConfigLoading(false);
      }
    };

    loadBillingConfig();
    console.log('✅ Billing config useEffect setup complete');
  }, []);

  // Helper functions to get pricing from dynamic configuration
  const getBasePlanPrice = (accountType) => {
    if (billingConfig?.plans && accountType) {
      const plan = billingConfig.plans[accountType];
      if (plan) return plan.monthly_cost;
    }
    
    // Fallback pricing from constants if config not loaded
    return BASE_PLAN_PRICING[accountType] || (accountType === 'trial' ? 0 : BASE_PLAN_PRICING.solo);
  };

  const getPhoneSlotPrice = () => {
    if (billingConfig?.phone?.monthly_cost_per_slot) {
      return billingConfig.phone.monthly_cost_per_slot;
    }
    return PHONE_ADDON.MONTHLY_COST_PER_SLOT;
  };

  // Calculate price based on billing cycle
  const getPriceForBillingCycle = (monthlyPrice, cycle = 'monthly') => {
    if (cycle === 'annual' && monthlyPrice) {
      const annualPrice = monthlyPrice * 12 * (1 - ANNUAL_DISCOUNT);
      return Math.round(annualPrice / 12 * 100) / 100; // Monthly equivalent of annual price
    }
    return monthlyPrice;
  };

  const formatPriceForBillingCycle = (monthlyPrice, cycle = 'monthly') => {
    if (cycle === 'annual' && monthlyPrice) {
      const monthlyEquivalent = getPriceForBillingCycle(monthlyPrice, cycle);
      return `$${monthlyEquivalent}/mo (billed annually)`;
    }
    return `$${monthlyPrice}/mo`;
  };

  const getPhoneFeatures = () => {
    if (billingConfig?.phone?.features) {
      return billingConfig.phone.features;
    }
    // Fallback features
    return [
      'Hands-Free Schedule Management',
      'Closing-Smart Scheduling',
      'Seamless Multi-Party Coordination',
      'Proactive Closing Support',
      '24/7 Smart Assistant',
    ];
  };

  const getMaxPhoneSlots = (accountType) => {
    if (billingConfig?.phone?.max_slots_per_account?.[accountType]) {
      return billingConfig.phone.max_slots_per_account[accountType];
    }
    // Fallback max slots
    const fallbackMaxSlots = {
      solo: 3,
      solo_assistant: 5,
      small_team: 15,
      medium_team: 20,
      large_team: 30,
      small_brokerage: 50,
      medium_brokerage: 100,
      large_brokerage: 200
    };
    return fallbackMaxSlots[accountType] || 3;
  };

  const getRecommendedPhoneSlots = (accountType) => {
    if (billingConfig?.phone?.recommended_slots?.[accountType]) {
      return billingConfig.phone.recommended_slots[accountType];
    }
    // Fallback recommended slots
    const fallbackRecommendedSlots = {
      solo: 1,
      solo_assistant: 2,
      small_team: 4,
      medium_team: 6,
      large_team: 10,
      small_brokerage: 10,
      medium_brokerage: 20,
      large_brokerage: 40
    };
    return fallbackRecommendedSlots[accountType] || 1;
  };

  // Dynamic account types with pricing from configuration
  const accountTypes = [
    {
      key: 'solo',
      label: 'Solo',
      seats: ACCOUNT_TYPE_SEATS.solo,
      price: getBasePlanPrice('solo'),
      description: `For individual agents. Includes ${ACCOUNT_TYPE_SEATS.solo} seats.`
    },
    {
      key: 'solo_assistant',
      label: 'Solo + 1 Assistant',
      seats: ACCOUNT_TYPE_SEATS.solo_assistant,
      price: getBasePlanPrice('solo_assistant'),
      description: `For agents with an assistant. Includes ${ACCOUNT_TYPE_SEATS.solo_assistant} seats.`
    },
    {
      key: 'small_team',
      label: 'Small Team',
      seats: ACCOUNT_TYPE_SEATS.small_team,
      price: getBasePlanPrice('small_team'),
      description: `For small teams. Includes ${ACCOUNT_TYPE_SEATS.small_team} seats.`
    },
    {
      key: 'medium_team',
      label: 'Medium Team',
      seats: ACCOUNT_TYPE_SEATS.medium_team,
      price: getBasePlanPrice('medium_team'),
      description: `For medium teams. Includes ${ACCOUNT_TYPE_SEATS.medium_team} seats.`
    },
    {
      key: 'large_team',
      label: 'Large Team',
      seats: ACCOUNT_TYPE_SEATS.large_team,
      price: getBasePlanPrice('large_team'),
      description: `For large teams. Includes ${ACCOUNT_TYPE_SEATS.large_team} seats.`
    },
    {
      key: 'small_brokerage',
      label: 'Small Brokerage',
      seats: ACCOUNT_TYPE_SEATS.small_brokerage,
      price: getBasePlanPrice('small_brokerage'),
      description: `For small brokerages. Includes ${ACCOUNT_TYPE_SEATS.small_brokerage} seats.`
    },
    {
      key: 'medium_brokerage',
      label: 'Medium Brokerage',
      seats: ACCOUNT_TYPE_SEATS.medium_brokerage,
      price: getBasePlanPrice('medium_brokerage'),
      description: `For medium brokerages. Includes ${ACCOUNT_TYPE_SEATS.medium_brokerage} seats.`
    },
    {
      key: 'large_brokerage',
      label: 'Large Brokerage',
      seats: ACCOUNT_TYPE_SEATS.large_brokerage,
      price: getBasePlanPrice('large_brokerage'),
      description: `For large brokerages. Includes ${ACCOUNT_TYPE_SEATS.large_brokerage} seats.`
    },
    {
      key: 'brokerage',
      label: 'Brokerage',
      seats: ACCOUNT_TYPE_SEATS.brokerage,
      price: getBasePlanPrice('brokerage'),
      description: `For brokerages. Includes ${ACCOUNT_TYPE_SEATS.brokerage} seats.`
    }
  ];

  // Payment states for Elements
  const [payment, setPayment] = useState({ name: '' }); // Only need cardholder name now
  const [cardComplete, setCardComplete] = useState(false);
  const [cardError, setCardError] = useState(null);
  const stripe = useStripe();
  const elements = useElements();
  const [signupError, setSignupError] = useState("");
  const [signupLoading, setSignupLoading] = useState(false);
  const [paymentError, setPaymentError] = useState("");



  // Handle cardholder name change
  const handleCardChange = e => {
    const { name, value } = e.target;
    
    // Clear payment errors when user starts typing
    if (paymentError) setPaymentError("");
    
    setPayment({ ...payment, [name]: value });
  };

  // Handle Stripe Element changes
  const handleCardElementChange = (event) => {
    setCardComplete(event.complete);
    setCardError(event.error ? event.error.message : null);
    if (paymentError && !event.error) setPaymentError("");
  };

  // Validation helpers
  const validateName = (name) => name.trim().length > 2;

  // Check if payment form is valid
  const isPaymentValid = () => {
    return validateName(payment.name) && cardComplete && !cardError && agreedToTerms;
  };

  // User details validation functions
  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const validatePassword = (password) => {
    // Password must be at least 8 characters with at least one letter and one number
    return password.length >= 8 && /[A-Za-z]/.test(password) && /[0-9]/.test(password);
  };

  const validateFirstName = (name) => {
    return name.trim().length >= 2;
  };

  const validateLastName = (name) => {
    return name.trim().length >= 2;
  };

  const validatePasswordMatch = (password, confirmPassword) => {
    return password === confirmPassword && password.length > 0;
  };

  // Check if user details form is valid
  const isUserDetailsValid = () => {
    const basicValidation = validateEmail(userDetails.email) &&
           validateFirstName(userDetails.name) &&
           validateLastName(userDetails.last_name) &&
           validatePassword(userDetails.password) &&
           validatePasswordMatch(userDetails.password, confirmPassword);

    // Only require agreedToTerms for trial accounts
    return isTrialMode ? (basicValidation && agreedToTerms) : basicValidation;
  };

  // Handle user details input changes with validation
  const handleUserDetailsChange = (field, value) => {
    if (userDetailsError) setUserDetailsError('');
    if (passwordError) setPasswordError('');
    setUserDetails({ ...userDetails, [field]: value });
  };

  const markTouched = (field) => setTouched(prev => ({ ...prev, [field]: true }));

  const markAllTouched = () =>
    setTouched({ name: true, last_name: true, email: true, password: true, confirmPassword: true, terms: true });

  const isBasicFormValid = () =>
    validateFirstName(userDetails.name) &&
    validateLastName(userDetails.last_name) &&
    validateEmail(userDetails.email) &&
    validatePassword(userDetails.password) &&
    validatePasswordMatch(userDetails.password, confirmPassword);

  const fieldBorder = (isValid) => isValid ? '#e2e8f0' : '#ef4444';
  const fieldShadow = (isValid) => isValid ? 'none' : '0 0 0 1px #ef4444';

  // Helper to update step
  const updateStep = (newStep) => {
    // Skip phone step if phone feature is disabled
    if (!FEATURE_FLAGS.PHONE_FEATURE_ENABLED) {
      if (newStep === 2) {
        newStep = 3; // Skip to step 3 (summary) 
      } else if (newStep === 3 && step === 1) {
        newStep = 3; // From step 1, go directly to step 3
      }
    }
    
    // Skip payment step if in trial mode
    if (isTrialMode && newStep === 4) {
      // Skip payment step and go directly to summary/confirmation
      setShowConfirmation(true);
      return;
    }
    
    setStep(newStep);
    // Only reset confirmation if we're not already showing confirmation
    // This prevents the confirmation from being hidden after successful signup
    if (newStep !== 4 && !showConfirmation) {
      setShowConfirmation(false);
    }
  };

  // Step indicator component
  const renderStepIndicator = () => {
    let steps;
    if (isTrialMode) {
      // Trial mode: Plan -> Phone (optional) -> User Details -> Summary
      steps = FEATURE_FLAGS.PHONE_FEATURE_ENABLED ? [1, 2, 3] : [1, 3];
    } else {
      // Regular mode: Plan -> Phone (optional) -> User Details -> Payment -> Summary
      steps = FEATURE_FLAGS.PHONE_FEATURE_ENABLED ? [1, 2, 3, 4] : [1, 3, 4];
    }
    
    return (
      <div style={styles.stepIndicator}>
        {steps.map(stepNumber => (
          <div
            key={stepNumber}
            style={{
              ...styles.stepDot,
              ...(stepNumber === step ? styles.stepDotActive : {}),
              ...(stepNumber < step ? styles.stepDotCompleted : {}),
            }}
          />
        ))}
      </div>
    );
  };

  // Step 1: Account Type Selection - Enhanced UI
  const renderAccountTypeStep = () => {
    const teamSizeOptions = [
      {
        key: 'small_team',
        label: 'Small Team',
        subtitle: `${ACCOUNT_TYPE_SEATS.small_team} seats included`,
        description: 'Perfect for growing teams',
        seats: ACCOUNT_TYPE_SEATS.small_team,
        price: getBasePlanPrice('small_team'),
        recommended: false
      },
      {
        key: 'medium_team',
        label: 'Medium Team',
        subtitle: `${ACCOUNT_TYPE_SEATS.medium_team} seats included`,
        description: 'Most popular choice',
        seats: ACCOUNT_TYPE_SEATS.medium_team,
        price: getBasePlanPrice('medium_team'),
        recommended: true
      },
      {
        key: 'large_team',
        label: 'Large Team',
        subtitle: `${ACCOUNT_TYPE_SEATS.large_team} seats included`,
        description: 'For established teams',
        seats: ACCOUNT_TYPE_SEATS.large_team,
        price: getBasePlanPrice('large_team'),
        recommended: false
      }
    ];

    const brokerageSizeOptions = [
      {
        key: 'small_brokerage',
        label: 'Small Brokerage',
        subtitle: `${ACCOUNT_TYPE_SEATS.small_brokerage} seats included`,
        description: 'Perfect for boutique brokerages',
        seats: ACCOUNT_TYPE_SEATS.small_brokerage,
        price: getBasePlanPrice('small_brokerage'),
        recommended: false
      },
      {
        key: 'medium_brokerage',
        label: 'Medium Brokerage',
        subtitle: `${ACCOUNT_TYPE_SEATS.medium_brokerage} seats included`,
        description: 'Most popular for growing brokerages',
        seats: ACCOUNT_TYPE_SEATS.medium_brokerage,
        price: getBasePlanPrice('medium_brokerage'),
        recommended: true
      },
      {
        key: 'large_brokerage',
        label: 'Large Brokerage',
        subtitle: `${ACCOUNT_TYPE_SEATS.large_brokerage} seats included`,
        description: 'For established enterprise brokerages',
        seats: ACCOUNT_TYPE_SEATS.large_brokerage,
        price: getBasePlanPrice('large_brokerage'),
        recommended: false
      }
    ];

    // Primary plan categories for better UX
    const primaryPlans = [
      // Add trial plan if feature is enabled
      ...(isTrialFeatureEnabled() ? [{
        key: 'trial',
        label: TRIAL_CONFIG.PLAN_DETAILS.displayName,
        icon: '⏱️',
        subtitle: TRIAL_CONFIG.PLAN_DETAILS.period,
        description: TRIAL_CONFIG.PLAN_DETAILS.description,
        seats: TRIAL_CONFIG.PLAN_DETAILS.features.find(f => f.includes('seats'))?.match(/\d+/)?.[0] || '1',
        price: TRIAL_CONFIG.PLAN_DETAILS.price,
        isTrial: true,
        isRecommended: true
      }] : []),
      {
        key: 'solo',
        label: 'Solo',
        icon: '👤',
        subtitle: 'Individual Agent',
        description: 'Perfect for individual real estate agents',
        seats: ACCOUNT_TYPE_SEATS.solo,
        price: getBasePlanPrice('solo'),
        isTrial: false
      },
      {
        key: 'solo_assistant',
        label: 'Solo + Assistant',
        icon: '👥',
        subtitle: 'Agent + 1 Assistant',
        description: 'For agents working with an assistant',
        seats: ACCOUNT_TYPE_SEATS.solo_assistant,
        price: getBasePlanPrice('solo_assistant')
      },
      {
        key: 'team_category',
        label: 'Team',
        icon: '🏢',
        subtitle: ['small_team', 'medium_team', 'large_team'].includes(selectedType) 
          ? teamSizeOptions.find(t => t.key === selectedType)?.label + ' Selected'
          : 'Small to Large Teams',
        description: ['small_team', 'medium_team', 'large_team'].includes(selectedType)
          ? teamSizeOptions.find(t => t.key === selectedType)?.description
          : 'Click to choose your team size',
        isTeamCategory: true,
        priceRange: ['small_team', 'medium_team', 'large_team'].includes(selectedType)
          ? `$${teamSizeOptions.find(t => t.key === selectedType)?.price}`
          : `$${getBasePlanPrice('small_team')} - $${getBasePlanPrice('large_team')}`,
        seatsRange: ['small_team', 'medium_team', 'large_team'].includes(selectedType)
          ? `${teamSizeOptions.find(t => t.key === selectedType)?.seats} seats`
          : `${ACCOUNT_TYPE_SEATS.small_team} - ${ACCOUNT_TYPE_SEATS.large_team} seats`
      },
      {
        key: 'brokerage_category',
        label: 'Brokerage',
        icon: '🏛️',
        subtitle: ['small_brokerage', 'medium_brokerage', 'large_brokerage'].includes(selectedType)
          ? brokerageSizeOptions.find(b => b.key === selectedType)?.label + ' Selected'
          : 'Enterprise Solution',
        description: ['small_brokerage', 'medium_brokerage', 'large_brokerage'].includes(selectedType)
          ? brokerageSizeOptions.find(b => b.key === selectedType)?.description
          : 'Click to choose your brokerage size',
        isBrokerageCategory: true,
        priceRange: ['small_brokerage', 'medium_brokerage', 'large_brokerage'].includes(selectedType)
          ? `$${brokerageSizeOptions.find(b => b.key === selectedType)?.price}`
          : `$${getBasePlanPrice('small_brokerage')} - $${getBasePlanPrice('large_brokerage')}`,
        seatsRange: ['small_brokerage', 'medium_brokerage', 'large_brokerage'].includes(selectedType)
          ? `${brokerageSizeOptions.find(b => b.key === selectedType)?.seats} seats`
          : `${ACCOUNT_TYPE_SEATS.small_brokerage} - ${ACCOUNT_TYPE_SEATS.large_brokerage} seats`
      }
    ];

    const handlePlanSelection = (planKey) => {
      if (planKey === 'trial') {
        setSelectedType('trial');
        setIsTrialMode(true);
        setSkipPaymentMethod(true);
      } else if (planKey === 'team_category') {
        // Always show modal for team selection, even if a team is already selected
        setShowTeamModal(true);
        setIsTrialMode(false);
        setSkipPaymentMethod(false);
      } else if (planKey === 'brokerage_category') {
        // Always show modal for brokerage selection, even if a brokerage is already selected
        setShowBrokerageModal(true);
        setIsTrialMode(false);
        setSkipPaymentMethod(false);
      } else {
        // For solo plans, directly set the selection (this clears any previous selection)
        setSelectedType(planKey);
        setIsTrialMode(false);
        setSkipPaymentMethod(false);
      }
    };

    const handleTeamSelection = (teamType) => {
      setSelectedType(teamType);
      setShowTeamModal(false);
    };

    const handleBrokerageSelection = (brokerageType) => {
      setSelectedType(brokerageType);
      setShowBrokerageModal(false);
    };

    return (
      <div style={styles.stepContainer}>
        {renderStepIndicator()}
        <h2 style={styles.title}>Choose Your Plan</h2>
        <p style={{
          ...styles.subtitle,
          marginTop: '0px',
          marginBottom: '1px'
        }}>
          Choose the plan that fits your business.
        </p>
        
        {/* Top Navigation Buttons */}
        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 2, marginBottom: 4 }}>
          <Button3D
            style={{ width: 140, fontSize: 16 }}
            onClick={() => navigate('/', { state: { openLogin: true } })}
          >
            Back
          </Button3D>
          <Button3D
            style={{ 
              width: 140, 
              fontSize: 16,
              opacity: !selectedType ? 0.5 : 1,
              cursor: !selectedType ? 'not-allowed' : 'pointer'
            }}
            disabled={!selectedType}
            onClick={() => {
              if (isTrialMode) {
                // Trial mode: go to phone step (if enabled) or user details step
                updateStep(FEATURE_FLAGS.PHONE_FEATURE_ENABLED ? 2 : 3);
              } else {
                // Regular mode: proceed to phone or user details step
                updateStep(FEATURE_FLAGS.PHONE_FEATURE_ENABLED ? 2 : 3);
              }
            }}
          >
            Next
          </Button3D>
        </div>
        
        {/* Primary Plan Cards */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', // Increased from 280px
          gap: '12px',
          marginBottom: '1px',
          paddingTop: '2px'
        }}>
          {primaryPlans.map(plan => {
            const isSelected = plan.isTeamCategory 
              ? ['small_team', 'medium_team', 'large_team'].includes(selectedType)
              : plan.isBrokerageCategory
              ? ['small_brokerage', 'medium_brokerage', 'large_brokerage'].includes(selectedType)
              : selectedType === plan.key;
            
            return (
              <div
                key={plan.key}
                style={{
                  background: isSelected 
                    ? (plan.isTrial 
                        ? 'linear-gradient(135deg, #ecfdf5 0%, #d1fae5 100%)' // Green gradient when selected
                        : 'linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%)')
                    : 'linear-gradient(135deg, #f1f5f9 0%, #e2e8f0 100%)', // Same neutral style for all unselected
                  border: isSelected 
                    ? (plan.isTrial ? '2px solid #10b981' : '2px solid #3b82f6') // Green border when selected
                    : '1px solid #e2e8f0', // Same neutral border for all unselected
                  borderRadius: '16px',
                  padding: '13px 16px 10px 16px',
                  cursor: 'pointer',
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  boxShadow: isSelected 
                    ? (plan.isTrial 
                        ? '0 8px 25px rgba(16, 185, 129, 0.2), 0 0 0 1px rgba(16, 185, 129, 0.1)' // Green shadow when selected
                        : '0 8px 25px rgba(59, 130, 246, 0.15), 0 0 0 1px rgba(59, 130, 246, 0.1)')
                    : '0 2px 8px rgba(0, 0, 0, 0.04)', // Same neutral shadow for all unselected
                  transform: isSelected ? 'translateY(-2px)' : 'translateY(0)',
                  minHeight: '96px',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'space-between',
                  position: 'relative',
                  overflow: 'hidden'
                }}
                onClick={() => handlePlanSelection(plan.key)}
                onMouseEnter={(e) => {
                  if (!isSelected) {
                    e.currentTarget.style.transform = 'translateY(-1px)';
                    e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.08)';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!isSelected) {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.04)';
                  }
                }}
              >
                {/* Recommended badge for trial plan */}
                {plan.isRecommended && (
                  <div style={{
                    position: 'absolute',
                    top: '-30px',
                    left: '50%',
                    transform: 'translateX(-50%)',
                    background: isSelected 
                      ? 'linear-gradient(135deg, #10b981 0%, #059669 100%)' 
                      : 'linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)',
                    color: 'white',
                    fontSize: '12px',
                    fontWeight: '600',
                    padding: '4px 16px',
                    borderRadius: '12px',
                    boxShadow: isSelected 
                      ? '0 2px 8px rgba(16, 185, 129, 0.3)' 
                      : '0 2px 8px rgba(99, 102, 241, 0.3)',
                    zIndex: 10
                  }}>
                    RECOMMENDED
                  </div>
                )}
                
                {/* Header */}
                <div>
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    marginBottom: '2px'
                  }}>
                    <span style={{
                      fontSize: '20px',
                      marginRight: '7px'
                    }}>{plan.icon}</span>
                    <div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                        <h3 style={{
                          fontSize: '16px',
                          fontWeight: '700',
                          color: '#1e293b',
                          margin: '0',
                          letterSpacing: '-0.01em'
                        }}>{plan.label}</h3>
                        {plan.isTrial && (
                          <span className="trial-badge">FREE TRIAL</span>
                        )}
                      </div>
                      <p style={{
                        fontSize: '12px',
                        color: '#64748b',
                        margin: 0,
                        fontWeight: '500'
                      }}>{plan.subtitle}</p>
                    </div>
                  </div>
                  
                  <p style={{
                    fontSize: '13px',
                    color: '#64748b',
                    lineHeight: '1.3',
                    margin: '0 0 2px 0'
                  }}>
                    {plan.isTrial 
                      ? 'Get started free — no credit card needed' 
                      : plan.description
                    }
                  </p>
                </div>

                {/* Pricing */}
                <div style={{
                  borderTop: '1px solid #e2e8f0',
                  paddingTop: '6px',
                  marginTop: '4px'
                }}>
                  {plan.isTeamCategory || plan.isBrokerageCategory ? (
                    <div>
                      <div style={{
                        fontSize: '17px',
                        fontWeight: '700',
                        color: isSelected ? '#3b82f6' : '#1e293b',
                        marginBottom: '1px'
                      }}>{plan.priceRange}/mo</div>
                      <div style={{
                        fontSize: '12px',
                        color: '#64748b',
                        fontWeight: '500'
                      }}>{plan.seatsRange}</div>
                    </div>
                  ) : (
                    <div style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center'
                    }}>
                      {plan.isTrial ? (
                        // Special display for trial plans
                        <div>
                          <div style={{
                            fontSize: '19px',
                            fontWeight: '700',
                            color: '#10b981',
                            letterSpacing: '-0.02em'
                          }}>FREE</div>
                          {/* <div style={{
                            fontSize: '10px',
                            color: '#64748b',
                            fontWeight: '500'
                          }}>14-day trial</div> */}
                        </div>
                      ) : (
                        // Regular display for paid plans
                        <div>
                          <div style={{
                            fontSize: '18px',
                            fontWeight: '700',
                            color: isSelected ? '#10b981' : '#1e293b',
                            letterSpacing: '-0.02em'
                          }}>${plan.price}/mo</div>
                          <div style={{
                            fontSize: '12px',
                            color: '#64748b',
                            fontWeight: '500'
                          }}>${(plan.price / plan.seats).toFixed(2)} per seat</div>
                        </div>
                      )}
                      <div style={{
                        background: isSelected 
                          ? (plan.isTrial 
                              ? 'linear-gradient(135deg, #10b981 0%, #059669 100%)' 
                              : 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)')
                          : 'linear-gradient(135deg, #64748b 0%, #475569 100%)',
                        color: '#ffffff',
                        padding: '3px 8px',
                        borderRadius: '8px',
                        fontSize: '13px',
                        fontWeight: '600',
                        boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)'
                      }}>{plan.seats} seats</div>
                    </div>
                  )}
                </div>

                {/* Selected badge for solo and trial plans */}
                {isSelected && !plan.isTeamCategory && !plan.isBrokerageCategory && (
                  <div style={{
                    position: 'absolute',
                    top: '-8px',
                    left: '50%',
                    transform: 'translateX(-50%)',
                    background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
                    color: 'white',
                    fontSize: '12px',
                    fontWeight: '600',
                    padding: '4px 16px',
                    borderRadius: '12px',
                    boxShadow: '0 2px 8px rgba(59, 130, 246, 0.3)'
                  }}>
                    SELECTED
                  </div>
                )}

                {/* Selection indicator */}
                {isSelected && (
                  <>
                    <div style={{
                      position: 'absolute',
                      top: '10px',
                      right: '10px',
                      width: '18px',
                      height: '18px',
                      background: '#3b82f6',
                      borderRadius: '50%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      boxShadow: '0 2px 4px rgba(59, 130, 246, 0.3)'
                    }}>
                      <span style={{ color: 'white', fontSize: '12px', fontWeight: 'bold' }}>✓</span>
                    </div>
                    
                    {/* Selection badge for Team/Brokerage categories */}
                    {(plan.isTeamCategory && ['small_team', 'medium_team', 'large_team'].includes(selectedType)) && (
                      <div style={{
                        position: 'absolute',
                        top: '10px',
                        right: '35px',
                        background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                        color: 'white',
                        padding: '3px 10px',
                        borderRadius: '10px',
                        fontSize: '12px',
                        fontWeight: '600',
                        boxShadow: '0 2px 8px rgba(16, 185, 129, 0.3)',
                        zIndex: 10
                      }}>
                        {teamSizeOptions.find(t => t.key === selectedType)?.label} Selected
                      </div>
                    )}
                    
                    {(plan.isBrokerageCategory && ['small_brokerage', 'medium_brokerage', 'large_brokerage'].includes(selectedType)) && (
                      <div style={{
                        position: 'absolute',
                        top: '10px',
                        right: '35px',
                        background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                        color: 'white',
                        padding: '3px 10px',
                        borderRadius: '10px',
                        fontSize: '12px',
                        fontWeight: '600',
                        boxShadow: '0 2px 8px rgba(16, 185, 129, 0.3)',
                        zIndex: 10
                      }}>
                        {brokerageSizeOptions.find(b => b.key === selectedType)?.label} Selected
                      </div>
                    )}
                  </>
                )}
              </div>
            );
          })}
        </div>

        {/* Team Size Selection Modal */}
        <Modal 
          show={showTeamModal} 
          onHide={() => setShowTeamModal(false)} 
          centered 
          size="lg"
          style={{zIndex: 2000}}
          backdrop="static"
        >
          <Modal.Body style={{padding: 0, border: 'none', margin: 0}}>
            <div style={{
              background: 'white',
              borderRadius: '20px',
              padding: '24px',
              width: '820px',
              maxWidth: '90vw',
              maxHeight: '90vh',
              overflow: 'auto',
              boxShadow: '0 20px 60px rgba(0, 0, 0, 0.2)',
              position: 'relative',
              margin: '0 auto'
            }}>
              {/* Modal Header */}
              <div style={{
                textAlign: 'center',
                marginBottom: '20px',
                position: 'relative'
              }}>
                <button
                  onClick={() => {
                    setShowTeamModal(false);
                  }}
                  style={{
                    position: 'absolute',
                    left: 0,
                    top: 0,
                    background: 'none',
                    border: 'none',
                    fontSize: '14px',
                    color: '#64748b',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px'
                  }}
                >
                  ← Back
                </button>
                <h3 style={{
                  fontSize: '20px',
                  fontWeight: '700',
                  color: '#1e293b',
                  margin: '0 0 4px 0',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px'
                }}>
                  Choose Your Team Size
                  <TeamNotificationTooltip placement="bottom">
                    <span style={{ 
                      color: '#3b82f6', 
                      cursor: 'help',
                      fontSize: '18px'
                    }}>
                      ℹ️
                    </span>
                  </TeamNotificationTooltip>
                </h3>
                <p style={{
                  fontSize: '13px',
                  color: '#64748b',
                  margin: 0
                }}>Select the team plan that best fits your organization</p>
              </div>

              {/* Close button */}
              <button
                onClick={() => setShowTeamModal(false)}
                style={{
                  position: 'absolute',
                  top: '20px',
                  right: '20px',
                  background: 'none',
                  border: 'none',
                  fontSize: '24px',
                  color: '#64748b',
                  cursor: 'pointer',
                  padding: '8px',
                  borderRadius: '50%',
                  transition: 'all 0.2s ease'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = '#f1f5f9';
                  e.currentTarget.style.color = '#1e293b';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'none';
                  e.currentTarget.style.color = '#64748b';
                }}
              >×</button>

              {/* Team Options */}
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
                gap: '12px',
                marginBottom: '16px'
              }}>
                {teamSizeOptions.map(team => (
                  <div
                    key={team.key}
                    style={{
                      background: selectedType === team.key 
                        ? 'linear-gradient(135deg, #ecfdf5 0%, #d1fae5 100%)' 
                        : 'linear-gradient(135deg, #f1f5f9 0%, #e2e8f0 100%)',
                      border: selectedType === team.key ? '2px solid #10b981' : '1px solid #e2e8f0',
                      borderRadius: '12px',
                      padding: '10px',
                      cursor: 'pointer',
                      transition: 'all 0.3s ease',
                      position: 'relative',
                      textAlign: 'center',
                      transform: selectedType === team.key ? 'translateY(-2px)' : 'translateY(0)',
                      boxShadow: selectedType === team.key ? '0 8px 25px rgba(0, 0, 0, 0.1)' : 'none'
                    }}
                    onClick={() => handleTeamSelection(team.key)}
                    onMouseEnter={(e) => {
                      if (selectedType !== team.key) {
                        e.currentTarget.style.transform = 'translateY(-2px)';
                        e.currentTarget.style.boxShadow = '0 8px 25px rgba(0, 0, 0, 0.1)';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (selectedType !== team.key) {
                        e.currentTarget.style.transform = 'translateY(0)';
                        e.currentTarget.style.boxShadow = 'none';
                      }
                    }}
                  >

                    {/* Selected Badge */}
                    {selectedType === team.key && (
                      <div style={{
                        position: 'absolute',
                        top: '-8px',
                        left: '50%',
                        transform: 'translateX(-50%)',
                        background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
                        color: 'white',
                        fontSize: '12px',
                        fontWeight: '600',
                        padding: '4px 16px',
                        borderRadius: '12px',
                        boxShadow: '0 2px 8px rgba(59, 130, 246, 0.3)'
                      }}>
                        SELECTED
                      </div>
                    )}

                    <h4 style={{
                      fontSize: '16px',
                      fontWeight: '700',
                      color: '#1e293b',
                      margin: '0 0 2px 0'
                    }}>{team.label}</h4>

                    <p style={{
                      fontSize: '12px',
                      color: '#64748b',
                      margin: '0 0 2px 0',
                      fontWeight: '500'
                    }}>{team.subtitle}</p>

                    <p style={{
                      fontSize: '13px',
                      color: '#64748b',
                      margin: '0 0 6px 0',
                      lineHeight: '1.2'
                    }}>{team.description}</p>

                    <div style={{
                      borderTop: '1px solid #e2e8f0',
                      paddingTop: '6px',
                      marginTop: '2px'
                    }}>
                      <div style={{
                        fontSize: '18px',
                        fontWeight: '700',
                        color: '#1e293b',
                        marginBottom: '1px'
                      }}>${team.price}/mo</div>
                      <div style={{
                        fontSize: '12px',
                        color: '#64748b',
                        fontWeight: '500'
                      }}>${(team.price / team.seats).toFixed(2)} per seat</div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Team Collaboration Information */}
              <div style={{
                margin: '8px 0',
                padding: '8px',
                background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)',
                borderRadius: '12px',
                border: '1px solid #e2e8f0'
              }}>
                <details style={{ margin: 0 }}>
                  <summary style={{ 
                    cursor: 'pointer', 
                    color: '#3b82f6', 
                    fontWeight: '600',
                    fontSize: '16px',
                    marginBottom: '1px',
                    listStyle: 'none',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '1px',
                    justifyContent: 'center'
                  }}>
                    <span>🤝</span>
                    How does team collaboration work?
                    <span style={{ fontSize: '12px', color: '#64748b' }}>▼</span>
                  </summary>
                  <div style={{ marginTop: '12px' }}>
                    <TeamCollaborationExplainer 
                      showTitle={false}
                      compact={true}
                      placement="modal"
                    />
                  </div>
                </details>
              </div>

              {/* Modal Footer */}
              <div style={{
                textAlign: 'center'
              }}>
                <Button3D
                  style={{ 
                    width: 140, 
                    fontSize: 16,
                    background: 'linear-gradient(90deg, #a7a9ac 0%, #bfc1c2 40%, #888a8c 100%)',
                    border: 'none',
                    color: '#333',
                    boxShadow: '0 3px 5px rgba(0,0,0,0.2)',
                    transition: 'all 0.2s ease-in-out',
                    borderRadius: '4px',
                    fontWeight: '500',
                    padding: '6px 24px 8px 24px'
                  }}
                  onClick={() => setShowTeamModal(false)}
                >
                  Cancel
                </Button3D>
              </div>
            </div>
          </Modal.Body>
        </Modal>

        {/* Brokerage Size Selection Modal */}
        <Modal 
          show={showBrokerageModal} 
          onHide={() => setShowBrokerageModal(false)} 
          centered 
          size="lg"
          style={{zIndex: 2000}}
          backdrop="static"
        >
          <Modal.Body style={{padding: 0, border: 'none', margin: 0}}>
            <div style={{
              background: 'white',
              borderRadius: '20px',
              padding: '24px',
              width: '820px',
              maxWidth: '90vw',
              maxHeight: '90vh',
              overflow: 'auto',
              boxShadow: '0 20px 60px rgba(0, 0, 0, 0.2)',
              position: 'relative',
              margin: '0 auto'
            }}>
              {/* Modal Header */}
              <div style={{
                textAlign: 'center',
                marginBottom: '20px',
                position: 'relative'
              }}>
                <button
                  onClick={() => {
                    setShowBrokerageModal(false);
                  }}
                  style={{
                    position: 'absolute',
                    left: 0,
                    top: 0,
                    background: 'none',
                    border: 'none',
                    fontSize: '14px',
                    color: '#64748b',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px'
                  }}
                >
                  ← Back
                </button>
                <h3 style={{
                  fontSize: '20px',
                  fontWeight: '700',
                  color: '#1e293b',
                  margin: '0 0 4px 0'
                }}>Choose Your Brokerage Size</h3>
                <p style={{
                  fontSize: '13px',
                  color: '#64748b',
                  margin: 0
                }}>Select the brokerage plan that best fits your business</p>
              </div>

              {/* Close button */}
              <button
                onClick={() => setShowBrokerageModal(false)}
                style={{
                  position: 'absolute',
                  top: '20px',
                  right: '20px',
                  background: 'none',
                  border: 'none',
                  fontSize: '24px',
                  color: '#64748b',
                  cursor: 'pointer',
                  padding: '8px',
                  borderRadius: '50%',
                  transition: 'all 0.2s ease'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = '#f1f5f9';
                  e.currentTarget.style.color = '#1e293b';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'none';
                  e.currentTarget.style.color = '#64748b';
                }}
              >×</button>

              {/* Brokerage Options */}
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
                gap: '12px',
                marginBottom: '16px'
              }}>
                {brokerageSizeOptions.map(brokerage => (
                  <div
                    key={brokerage.key}
                    style={{
                      background: selectedType === brokerage.key 
                        ? 'linear-gradient(135deg, #ecfdf5 0%, #d1fae5 100%)' 
                        : 'linear-gradient(135deg, #f1f5f9 0%, #e2e8f0 100%)',
                      border: selectedType === brokerage.key ? '2px solid #10b981' : '1px solid #e2e8f0',
                      borderRadius: '12px',
                      padding: '10px',
                      cursor: 'pointer',
                      transition: 'all 0.3s ease',
                      position: 'relative',
                      textAlign: 'center',
                      transform: selectedType === brokerage.key ? 'translateY(-2px)' : 'translateY(0)',
                      boxShadow: selectedType === brokerage.key ? '0 8px 25px rgba(0, 0, 0, 0.1)' : 'none'
                    }}
                    onClick={() => handleBrokerageSelection(brokerage.key)}
                    onMouseEnter={(e) => {
                      if (selectedType !== brokerage.key) {
                        e.currentTarget.style.transform = 'translateY(-2px)';
                        e.currentTarget.style.boxShadow = '0 8px 25px rgba(0, 0, 0, 0.1)';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (selectedType !== brokerage.key) {
                        e.currentTarget.style.transform = 'translateY(0)';
                        e.currentTarget.style.boxShadow = 'none';
                      }
                    }}
                  >

                    {/* Selected Badge */}
                    {selectedType === brokerage.key && (
                      <div style={{
                        position: 'absolute',
                        top: '-8px',
                        left: '50%',
                        transform: 'translateX(-50%)',
                        background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
                        color: 'white',
                        fontSize: '12px',
                        fontWeight: '600',
                        padding: '4px 16px',
                        borderRadius: '12px',
                        boxShadow: '0 2px 8px rgba(59, 130, 246, 0.3)'
                      }}>
                        SELECTED
                      </div>
                    )}

                    <h4 style={{
                      fontSize: '16px',
                      fontWeight: '700',
                      color: '#1e293b',
                      margin: '0 0 2px 0'
                    }}>{brokerage.label}</h4>

                    <p style={{
                      fontSize: '12px',
                      color: '#64748b',
                      margin: '0 0 2px 0',
                      fontWeight: '500'
                    }}>{brokerage.subtitle}</p>

                    <p style={{
                      fontSize: '13px',
                      color: '#64748b',
                      margin: '0 0 6px 0',
                      lineHeight: '1.2'
                    }}>{brokerage.description}</p>

                    <div style={{
                      borderTop: '1px solid #e2e8f0',
                      paddingTop: '6px',
                      marginTop: '2px'
                    }}>
                      <div style={{
                        fontSize: '18px',
                        fontWeight: '700',
                        color: '#1e293b',
                        marginBottom: '1px'
                      }}>${brokerage.price}/mo</div>
                      <div style={{
                        fontSize: '12px',
                        color: '#64748b',
                        fontWeight: '500'
                      }}>${(brokerage.price / brokerage.seats).toFixed(2)} per seat</div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Modal Footer */}
              <div style={{
                textAlign: 'center'
              }}>
                <Button3D
                  style={{ 
                    width: 140, 
                    fontSize: 16,
                    background: 'linear-gradient(90deg, #a7a9ac 0%, #bfc1c2 40%, #888a8c 100%)',
                    border: 'none',
                    color: '#333',
                    boxShadow: '0 3px 5px rgba(0,0,0,0.2)',
                    transition: 'all 0.2s ease-in-out',
                    borderRadius: '4px',
                    fontWeight: '500',
                    padding: '6px 24px 8px 24px'
                  }}
                  onClick={() => setShowBrokerageModal(false)}
                >
                  Cancel
                </Button3D>
              </div>
            </div>
          </Modal.Body>
        </Modal>

        {/* Navigation Buttons */}
        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 8 }}>
          <Button3D
            style={{ width: 140, fontSize: 16 }}
            onClick={() => navigate('/', { state: { openLogin: true } })}
          >
            Back
          </Button3D>
          <Button3D
            style={{ 
              width: 140, 
              fontSize: 16,
              opacity: !selectedType ? 0.5 : 1,
              cursor: !selectedType ? 'not-allowed' : 'pointer'
            }}
            disabled={!selectedType}
            onClick={() => {
              if (isTrialMode) {
                // Trial mode: go to phone step (if enabled) or user details step
                updateStep(FEATURE_FLAGS.PHONE_FEATURE_ENABLED ? 2 : 3);
              } else {
                // Regular mode: proceed to phone or user details step
                updateStep(FEATURE_FLAGS.PHONE_FEATURE_ENABLED ? 2 : 3);
              }
            }}
          >
            Next
          </Button3D>
        </div>
      </div>
    );
  };

  // Step 2: User Details
  const renderUserDetailsStep = () => (
    <div style={styles.stepContainer}>
      {renderStepIndicator()}
      <h2 style={styles.title}>Create Your Account</h2>
      <p style={styles.subtitle}>
        Enter your details to set up your SmartClose account.
      </p>
      
      <div style={styles.formSection}>
        <h3 style={styles.sectionTitle}>📝 Account Information</h3>

        {/* Row 1: First name + Last name */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '10px' }}>
          <input
            style={{
              ...styles.input,
              marginBottom: 0,
              borderColor: fieldBorder(!touched.name || validateFirstName(userDetails.name)),
              boxShadow: fieldShadow(!touched.name || validateFirstName(userDetails.name)),
            }}
            type="text"
            placeholder="First name"
            value={userDetails.name}
            onChange={e => handleUserDetailsChange('name', e.target.value)}
            onBlur={() => markTouched('name')}
          />
          <input
            style={{
              ...styles.input,
              marginBottom: 0,
              borderColor: fieldBorder(!touched.last_name || validateLastName(userDetails.last_name)),
              boxShadow: fieldShadow(!touched.last_name || validateLastName(userDetails.last_name)),
            }}
            type="text"
            placeholder="Last name"
            value={userDetails.last_name}
            onChange={e => handleUserDetailsChange('last_name', e.target.value)}
            onBlur={() => markTouched('last_name')}
          />
        </div>

        {/* Row 2: Email (full width) */}
        <div style={{ marginBottom: '10px' }}>
          <input
            style={{
              ...styles.input,
              marginBottom: 0,
              borderColor: fieldBorder(!touched.email || validateEmail(userDetails.email)),
              boxShadow: fieldShadow(!touched.email || validateEmail(userDetails.email)),
            }}
            type="email"
            placeholder="Email address"
            value={userDetails.email}
            onChange={e => handleUserDetailsChange('email', e.target.value)}
            onBlur={() => markTouched('email')}
          />
        </div>

        {/* Row 2: Password (full width) */}
        <div style={{ position: 'relative', marginBottom: '10px' }}>
          <input
            style={{
              ...styles.input,
              marginBottom: 0,
              paddingRight: '40px',
              borderColor: touched.password && !validatePassword(userDetails.password) ? '#ef4444'
                : userDetails.password && validatePassword(userDetails.password) ? '#10b981'
                : '#e2e8f0',
              boxShadow: touched.password && !validatePassword(userDetails.password) ? '0 0 0 1px #ef4444' : 'none',
            }}
            type={showPassword ? 'text' : 'password'}
            placeholder="Password (8+ characters with letters and numbers)"
            value={userDetails.password}
            onChange={e => handleUserDetailsChange('password', e.target.value)}
            onBlur={() => markTouched('password')}
          />
          <button
            type="button"
            onClick={() => setShowPassword(!showPassword)}
            style={{ position: 'absolute', right: '10px', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', padding: '5px', display: 'flex', alignItems: 'center', color: '#94a3b8' }}
            aria-label={showPassword ? 'Hide password' : 'Show password'}
          >
            {showPassword ? <FaEyeSlash size={16} /> : <FaEye size={16} />}
          </button>
        </div>

        {/* Row 3: Confirm Password (full width) */}
        <div style={{ position: 'relative', marginBottom: '10px' }}>
          <input
            style={{
              ...styles.input,
              marginBottom: 0,
              paddingRight: '40px',
              borderColor: touched.confirmPassword && !validatePasswordMatch(userDetails.password, confirmPassword) ? '#ef4444'
                : confirmPassword && validatePasswordMatch(userDetails.password, confirmPassword) ? '#10b981'
                : '#e2e8f0',
              boxShadow: touched.confirmPassword && !validatePasswordMatch(userDetails.password, confirmPassword) ? '0 0 0 1px #ef4444' : 'none',
            }}
            type={showConfirmPasswordField ? 'text' : 'password'}
            placeholder="Confirm password"
            value={confirmPassword}
            onChange={e => {
              if (passwordError) setPasswordError('');
              if (userDetailsError) setUserDetailsError('');
              setConfirmPassword(e.target.value);
            }}
            onBlur={() => markTouched('confirmPassword')}
          />
          <button
            type="button"
            onClick={() => setShowConfirmPasswordField(!showConfirmPasswordField)}
            style={{ position: 'absolute', right: '10px', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', padding: '5px', display: 'flex', alignItems: 'center', color: '#94a3b8' }}
            aria-label={showConfirmPasswordField ? 'Hide password' : 'Show password'}
          >
            {showConfirmPasswordField ? <FaEyeSlash size={16} /> : <FaEye size={16} />}
          </button>
        </div>

        {/* Row 4: Company (optional) */}
        <div style={{ position: 'relative' }}>
          <input
            style={{ ...styles.input, marginBottom: 0, paddingLeft: '36px' }}
            type="text"
            placeholder="Company / Brokerage (optional)"
            value={userDetails.company}
            onChange={e => handleUserDetailsChange('company', e.target.value)}
          />
          <span style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', fontSize: '14px', pointerEvents: 'none' }}>🏢</span>
        </div>
      </div>

      {passwordError && <div style={styles.errorText}>{passwordError}</div>}
      {userDetailsError && <div style={styles.errorText}>{userDetailsError}</div>}
      
      {/* Terms and Privacy Confirmation - Only show for trial accounts */}
      {isTrialMode && (
        <div style={{
          borderRadius: 12,
          padding: 10,
          background: isBasicFormValid() ? 'linear-gradient(135deg, #f1f5f9 0%, #e2e8f0 100%)' : '#f8fafc',
          border: touched.terms && !agreedToTerms ? '2px solid #ef4444' : '2px solid #e5e7eb',
          boxShadow: touched.terms && !agreedToTerms ? '0 0 0 1px #ef4444' : '0 4px 16px rgba(0,0,0,0.06)',
          marginBottom: 1,
          marginTop: 1,
          opacity: isBasicFormValid() ? 1 : 0.5,
          transition: 'all 0.2s ease',
        }}>
          <label style={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: '12px',
            cursor: isBasicFormValid() ? 'pointer' : 'not-allowed',
            fontSize: 14,
            lineHeight: '1.5',
            color: '#374151'
          }}>
            <input
              type="checkbox"
              checked={agreedToTerms}
              disabled={!isBasicFormValid()}
              onChange={(e) => setAgreedToTerms(e.target.checked)}
              style={{
                width: '20px',
                height: '20px',
                marginTop: '2px',
                accentColor: '#10b981',
                cursor: isBasicFormValid() ? 'pointer' : 'not-allowed',
              }}
            />
            <span>
              I confirm the information is correct and I agree to SmartClose's{' '}
              <a
                href="/privacy"
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  color: '#3b82f6',
                  textDecoration: 'none',
                  fontWeight: 600,
                  borderBottom: '1px dotted #3b82f6'
                }}
              >
                Terms of Service
              </a>
            </span>
          </label>
        </div>
      )}

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 6 }}>
        <Button3D
          style={{ width: 140, fontSize: 16 }}
          onClick={() => {
            // Go back to the appropriate previous step based on phone feature flag
            const previousStep = FEATURE_FLAGS.PHONE_FEATURE_ENABLED ? 2 : 1;
            updateStep(previousStep);
          }}
        >
          Back
        </Button3D>
        <div style={{ flex: 1 }} />
        <Button3D
          style={{ 
            width: 140, 
            fontSize: 16,
            opacity: !isUserDetailsValid() || signupLoading ? 0.5 : 1,
            cursor: !isUserDetailsValid() || signupLoading ? 'not-allowed' : 'pointer'
          }}
          disabled={!isUserDetailsValid() || signupLoading}
          onClick={async () => {
            // Mark all fields touched so invalid ones turn red
            markAllTouched();
            // Detailed validation with specific error messages
            if (!validateFirstName(userDetails.name)) {
              setUserDetailsError('Please enter your first name (at least 2 characters).');
              return;
            }
            if (!validateLastName(userDetails.last_name)) {
              setUserDetailsError('Please enter your last name (at least 2 characters).');
              return;
            }
            if (!validateEmail(userDetails.email)) {
              setUserDetailsError('Please enter a valid email address.');
              return;
            }
            if (!validatePassword(userDetails.password)) {
              setPasswordError('Password must be at least 8 characters with letters and numbers.');
              return;
            }
            if (!validatePasswordMatch(userDetails.password, confirmPassword)) {
              setPasswordError('Passwords do not match.');
              return;
            }
            if (isTrialMode && !agreedToTerms) {
              setUserDetailsError('Please agree to the Terms of Service and Privacy Policy.');
              return;
            }
            
            setPasswordError('');
            setUserDetailsError('');
            
            // For trial users, skip payment step and trigger signup process
            if (isTrialMode) {
              console.log('🚀 Starting trial signup process...');
              setSignupLoading(true);
              
              try {
                // Phase 1: Call signup API to create user account
                console.log('🚀 Starting Phase 1: Trial user account creation');
                console.log('🚀 Trial mode:', isTrialMode);
                console.log('🚀 Selected type:', selectedType);
                console.log('🚀 Account type being sent:', 'trial');
                
                const result = await signup({
                  name: userDetails.name,
                  last_name: userDetails.last_name,
                  email: userDetails.email,
                  password: userDetails.password,
                  company: userDetails.company || '',
                  accountType: 'trial',
                  phoneSlots: phoneSlots,
                  billingCycle: billingCycle,
                  paymentMethodId: null, // No payment method for trial
                  isTrialMode: true,
                  trialDurationDays: TRIAL_CONFIG.DURATION_DAYS
                });
                
                console.log('🔍 Trial signup result received:', result);
                console.log('🔍 Trial signup success:', result?.success);
                console.log('🔍 Trial signup message:', result?.message);
                console.log('🔍 Trial signup user:', result?.user);
                
                if (result && result.success) {
                  console.log('✅ Trial Phase 1 complete: User account created successfully');
                  
                  // Phase 1.5: Login user to get authentication token
                  console.log('🔐 Logging in trial user for authentication...');
                  const loginResult = await login({
                    email: userDetails.email,
                    password: userDetails.password
                  });
                  
                  console.log('🔍 Trial login result:', loginResult);
                  console.log('🔍 Trial login success:', loginResult?.success);
                  console.log('🔍 Trial login token:', loginResult?.token ? 'TOKEN_PRESENT' : 'NO_TOKEN');
                  console.log('🔍 Trial login user:', loginResult?.user ? 'USER_PRESENT' : 'NO_USER');
                  
                  if (loginResult && loginResult.success) {
                    console.log('✅ Trial user authenticated successfully');
                    // Store user and token for authenticated API calls
                    setCurrentUser(loginResult.user, loginResult.token);
                    
                    // Verify token was stored
                    const storedToken = localStorage.getItem('token');
                    console.log('🔍 Trial token stored in localStorage:', storedToken ? 'TOKEN_STORED' : 'NO_TOKEN_STORED');
                  } else {
                    console.warn('⚠️ Trial authentication failed after signup');
                    console.warn('Trial login error:', loginResult?.message);
                  }
                  
                  setSignupLoading(false);
                  setShowConfirmation(true);
                  sessionStorage.setItem('signup_showConfirmation', 'true');
                  sessionStorage.setItem('signup_selectedType', JSON.stringify(selectedType));
                  sessionStorage.setItem('signup_userDetails', JSON.stringify(userDetails));
                  sessionStorage.setItem('signup_phoneSlots', JSON.stringify(phoneSlots));
                  sessionStorage.setItem('signup_billingCycle', JSON.stringify(billingCycle));
                } else {
                  setSignupLoading(false);
                  if (result && result.error === 'TRIAL_EXPIRED_LOGIN_TO_UPGRADE') {
                    setUserDetailsError(
                      'Your free trial has expired. Please log in with your existing account to upgrade your plan.'
                    );
                  } else {
                    setUserDetailsError((result && result.message) || 'Trial signup failed.');
                  }
                  console.error('❌ Trial Phase 1 failed: Signup error:', result);
                }
              } catch (err) {
                setSignupLoading(false);
                setUserDetailsError('Unexpected error during trial signup.');
                console.error('Unexpected trial signup error:', err);
              }
            } else {
              updateStep(4);
            }
          }}
        >
          {signupLoading ? 'Processing...' : (isTrialMode ? 'Start Trial' : 'Next')}
        </Button3D>
      </div>
    </div>
  );

  // Step 3: Phone Addon
  const renderPhoneAddonStep = () => {
    const maxSlots = getMaxPhoneSlots(selectedType);
    const recommendedSlots = getRecommendedPhoneSlots(selectedType);
    const monthlyCostPerSlot = getPhoneSlotPrice();
    
    const totalMonthlyCost = phoneSlots * monthlyCostPerSlot;

    return (
      <div style={styles.stepContainer}>
        {renderStepIndicator()}
        <h2 style={styles.title}>Phone Slots</h2>
        <p style={styles.subtitle}>
          Stay ahead with AI-driven calendar control and effortless task scheduling by phone
        </p>

        {/* Phone Addon Card */}
        <div style={{
          border: '2px solid #e3f2fd',
          borderRadius: '12px',
          padding: '14px 16px',
          background: '#fafbfc',
          marginBottom: '8px'
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '16px' }}>
            <div>
              <h4 style={{ margin: 0, color: '#1976d2', fontWeight: 'bold' }}>
                📞 Phone Slots
              </h4>
              <p style={{ margin: '4px 0 0 0', color: '#666', fontSize: '14px' }}>
                ${monthlyCostPerSlot}/month per slot
              </p>
            </div>
            <div style={{
              background: '#e3f2fd',
              color: '#1976d2',
              padding: '4px 12px',
              borderRadius: '20px',
              fontSize: '12px',
              fontWeight: 'bold'
            }}>
              Recommended: {recommendedSlots} slot{recommendedSlots !== 1 ? 's' : ''}
            </div>
          </div>

          {/* Features */}
          <div style={{ marginBottom: '10px' }}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '8px' }}>
              {getPhoneFeatures().slice(0, 6).map((feature, idx) => (
                <div key={idx} style={{ display: 'flex', alignItems: 'center', fontSize: '13px', color: '#555' }}>
                  <span style={{ color: '#28a745', marginRight: '8px' }}>✓</span>
                  {feature}
                </div>
              ))}
            </div>
          </div>

          {/* Line Selection */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
            <div>
              <label style={{ fontWeight: '600', color: '#333', marginBottom: '4px', display: 'block' }}>
                Number of Phone Slots
              </label>
              <div style={{ fontSize: '13px', color: '#777' }}>
                Max {maxSlots} slots for {selectedType?.replace('_', ' ')} plan
              </div>
            </div>
            
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <button
                type="button"
                style={{
                  width: '36px',
                  height: '36px',
                  border: '2px solid #1976d2',
                  borderRadius: '50%',
                  background: phoneSlots <= 0 ? '#f5f5f5' : '#fff',
                  color: phoneSlots <= 0 ? '#ccc' : '#1976d2',
                  cursor: phoneSlots <= 0 ? 'not-allowed' : 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontWeight: 'bold'
                }}
                disabled={phoneSlots <= 0}
                onClick={() => setPhoneSlots(Math.max(0, phoneSlots - 1))}
              >
                −
              </button>
              
              <div style={{ 
                minWidth: '50px', 
                textAlign: 'center',
                fontSize: '24px',
                fontWeight: 'bold',
                color: '#1976d2'
              }}>
                {phoneSlots}
              </div>
              
              <button
                type="button"
                style={{
                  width: '36px',
                  height: '36px',
                  border: '2px solid #1976d2',
                  borderRadius: '50%',
                  background: phoneSlots >= maxSlots ? '#f5f5f5' : '#fff',
                  color: phoneSlots >= maxSlots ? '#ccc' : '#1976d2',
                  cursor: phoneSlots >= maxSlots ? 'not-allowed' : 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontWeight: 'bold'
                }}
                disabled={phoneSlots >= maxSlots}
                onClick={() => setPhoneSlots(Math.min(maxSlots, phoneSlots + 1))}
              >
                +
              </button>
            </div>
          </div>

          {/* Cost Summary */}
          {phoneSlots > 0 && (
            <div style={{
              background: '#e3f2fd',
              padding: '16px',
              borderRadius: '8px',
              border: '1px solid #bbdefb'
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                <span style={{ color: '#555' }}>Monthly cost ({phoneSlots} slot{phoneSlots !== 1 ? 's' : ''}):</span>
                <strong style={{ color: '#1976d2' }}>${totalMonthlyCost.toFixed(2)}/month</strong>
              </div>
              <hr style={{ margin: '8px 0', border: 'none', borderTop: '1px solid #90caf9' }} />
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span style={{ fontWeight: '600', color: '#333' }}>Total monthly:</span>
                <strong style={{ fontSize: '18px', color: '#1976d2' }}>
                  ${totalMonthlyCost.toFixed(2)}
                </strong>
              </div>
            </div>
          )}

          {/* Skip Option */}
          <div style={{ marginTop: '16px', textAlign: 'center' }}>
            <label style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={skipPhoneAddon}
                onChange={(e) => {
                  setSkipPhoneAddon(e.target.checked);
                  if (e.target.checked) setPhoneSlots(0);
                }}
                style={{ marginRight: '8px' }}
              />
              <span style={{ fontSize: '14px', color: '#666' }}>
                Skip phone addon for now (can be added later)
              </span>
            </label>
          </div>
        </div>

        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 8 }}>
          <Button3D
            style={{ width: 140, fontSize: 16 }}
            onClick={() => updateStep(1)}
          >
            Back
          </Button3D>
          <Button3D
            style={{ 
              width: 140, 
              fontSize: 16,
              opacity: (!skipPhoneAddon && phoneSlots === 0) ? 0.5 : 1,
              cursor: (!skipPhoneAddon && phoneSlots === 0) ? 'not-allowed' : 'pointer'
            }}
            disabled={!skipPhoneAddon && phoneSlots === 0}
            onClick={() => updateStep(3)}
          >
            Next
          </Button3D>
        </div>
      </div>
    );
  };

  // Step 4: Payment Method and Confirmation
  const renderPaymentStep = () => {
    const savedPhoneSlots = JSON.parse(sessionStorage.getItem('signup_phoneSlots')) || phoneSlots;
    const selectedPlan = getSelectedPlanDetails(selectedType);
    const isTrialPlan = selectedPlan?.isTrial || false;
    
    return (
      <div style={styles.stepContainer}>
        {renderStepIndicator()}
        <h2 style={styles.title}>Secure Payment and Confirmation</h2>
        {/* <p style={styles.subtitle}>
          Review your order, complete payment, and confirm your account details
        </p> */}
      
      {/* Account Review Section - First */}
      <div style={{
        ...styles.formSection,
        background: 'linear-gradient(135deg, #f1f5f9 0%, #e2e8f0 100%)',
        border: '1px solid #e2e8f0'
      }}>
        <h3 style={styles.sectionTitle}>📋 Account Review</h3>
        <p style={styles.sectionDescription}>
          Verify your account details
        </p>
        
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
          gap: 8,
          marginBottom: 1
        }}>
          {/* Plan Details */}
          <div style={{
            background: 'white',
            borderRadius: 12,
            padding: '10px',
            border: '1px solid #e2e8f0',
            boxShadow: '0 2px 8px rgba(0,0,0,0.04)'
          }}>
            <h4 style={{
              fontSize: 16, // Increased from 15
              fontWeight: 600,
              color: '#374151',
              margin: '0 0 8px 0',
              display: 'flex',
              alignItems: 'center',
              gap: '6px'
            }}>
              <span>💼</span> Plan Details
            </h4>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              marginBottom: 1,
              alignItems: 'center'
            }}>
              <span style={{ color: '#6b7280', fontWeight: 600, fontSize: 14 }}>Plan:</span> {/* Increased from 13 */}
              <span style={{
                padding: '4px 10px',
                background: isTrialPlan 
                  ? 'linear-gradient(135deg, #10b981 0%, #059669 100%)'
                  : 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
                color: 'white',
                borderRadius: 8,
                fontSize: 12, // Increased from 11
                fontWeight: 600,
                textTransform: 'uppercase'
              }}>
                {selectedPlan?.label}
              </span>
            </div>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              marginBottom: 1,
              alignItems: 'center'
            }}>
              <span style={{ color: '#6b7280', fontWeight: 600, fontSize: 14 }}>Price:</span> {/* Increased from 13 */}
              <span style={{ color: '#1f2937', fontWeight: 600, fontSize: 14 }}> {/* Increased from 13 */}
                {isTrialPlan ? 'Free Trial' : `$${selectedPlan?.price}/month`}
              </span>
            </div>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              marginBottom: 1,
              alignItems: 'center'
            }}>
              <span style={{ color: '#6b7280', fontWeight: 600, fontSize: 14 }}>Seats:</span> {/* Increased from 13 */}
              <span style={{ color: '#1f2937', fontWeight: 600, fontSize: 14 }}> {/* Increased from 13 */}
                {selectedPlan?.seats} seat{selectedPlan?.seats !== '1' ? 's' : ''}
              </span>
            </div>
            {FEATURE_FLAGS.PHONE_FEATURE_ENABLED && (
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}>
                <span style={{ color: '#6b7280', fontWeight: 600, fontSize: 14 }}>Phone Slots:</span> {/* Increased from 13 */}
                <span style={{ color: '#1f2937', fontWeight: 600, fontSize: 14 }}> {/* Increased from 13 */}
                  {savedPhoneSlots || 0} slot{(savedPhoneSlots || 0) !== 1 ? 's' : ''}
                </span>
              </div>
            )}
          </div>

          {/* Account Info */}
          <div style={{
            background: 'white',
            borderRadius: 12,
            padding: '10px',
            border: '1px solid #e2e8f0',
            boxShadow: '0 2px 8px rgba(0,0,0,0.04)'
          }}>
            <h4 style={{
              fontSize: 16, // Increased from 15
              fontWeight: 600,
              color: '#374151',
              margin: '0 0 8px 0',
              display: 'flex',
              alignItems: 'center',
              gap: '6px'
            }}>
              <span>👤</span> Account Info
            </h4>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              marginBottom: 1
            }}>
              <span style={{ color: '#6b7280', fontWeight: 600, fontSize: 14 }}>Name:</span> {/* Increased from 13 */}
              <span style={{ 
                color: '#1f2937', 
                fontWeight: 600, 
                fontSize: 14, // Increased from 13
                textAlign: 'right',
                maxWidth: '120px',
                wordBreak: 'break-word'
              }}>
                {userDetails.name}{userDetails.last_name ? ` ${userDetails.last_name}` : ''}
              </span>
            </div>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between'
            }}>
              <span style={{ color: '#6b7280', fontWeight: 600, fontSize: 14 }}>Email:</span> {/* Increased from 13 */}
              <span style={{ 
                color: '#1f2937', 
                fontWeight: 600, 
                fontSize: 13, // Increased from 13
                textAlign: 'right',
                maxWidth: '180px',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
              }}>
                {userDetails.email}
              </span>
            </div>
          </div>
        </div>

        {FEATURE_FLAGS.PHONE_FEATURE_ENABLED && savedPhoneSlots > 0 && (
          <div style={{
            background: 'linear-gradient(135deg, #ddd6fe 0%, #c4b5fd 100%)',
            borderRadius: 10,
            padding: 12,
            border: '1px solid #a78bfa',
            marginBottom: 6
          }}>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <span style={{
                color: '#5b21b6',
                fontSize: 14, // Increased from 13
                fontWeight: 600,
                display: 'flex',
                alignItems: 'center',
                gap: '4px'
              }}>
                📞 Phone Add-on
              </span>
              <span style={{
                color: '#581c87',
                fontWeight: 700,
                fontSize: 14 // Increased from 13
              }}>
                {savedPhoneSlots} line{savedPhoneSlots !== 1 ? 's' : ''}
              </span>
            </div>
          </div>
        )}

        {isTrialPlan && (
          <div style={{
            background: 'linear-gradient(135deg, #d1fae5 0%, #a7f3d0 100%)',
            borderRadius: 10,
            padding: 12,
            border: '1px solid #6ee7b7',
            marginTop: 12
          }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              marginBottom: 4
            }}>
              <span style={{ fontSize: '14px' }}>🎁</span>
              <span style={{
                color: '#047857',
                fontSize: 12,
                fontWeight: 600,
                textTransform: 'uppercase',
                letterSpacing: '0.5px'
              }}>
                Trial Benefits
              </span>
            </div>
            <p style={{
              color: '#065f46',
              fontSize: 11,
              margin: 0,
              lineHeight: '1.3'
            }}>
              14-day free trial • Full feature access • No commitment
            </p>
          </div>
        )}
      </div>

      {/* Order Summary */}
      {(phoneSlots > 0 || !skipPhoneAddon) && (
        <div style={{
          ...styles.formSection,
          background: 'linear-gradient(135deg, #f1f5f9 0%, #e2e8f0 100%)',
          border: '2px solid #e2e8f0'
        }}>
          <h3 style={styles.sectionTitle}>📋 Order Summary</h3>
          
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}>
            <span>{accountTypes.find(t => t.key === selectedType)?.label} Plan:</span>
            <span>{formatPriceForBillingCycle(accountTypes.find(t => t.key === selectedType)?.price, billingCycle)}</span>
          </div>
          
          {FEATURE_FLAGS.PHONE_FEATURE_ENABLED && phoneSlots > 0 && (
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}>
              <span>Phone Slots ({phoneSlots} slot{phoneSlots !== 1 ? 's' : ''}):</span>
              <span>{formatPriceForBillingCycle(phoneSlots * getPhoneSlotPrice(), billingCycle)}</span>
            </div>
          )}
          
          <hr style={{ margin: '8px 0' }} />
          
          <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 'bold' }}>
            <span>Total {billingCycle === 'annual' ? 'Monthly Equivalent' : 'Monthly'}:</span>
            <span>{formatPriceForBillingCycle((accountTypes.find(t => t.key === selectedType)?.price + (FEATURE_FLAGS.PHONE_FEATURE_ENABLED ? phoneSlots * getPhoneSlotPrice() : 0)), billingCycle)}</span>
          </div>
          
          {billingCycle === 'annual' && (
            <div style={{ 
              fontSize: '14px', 
              color: '#059669', 
              textAlign: 'center', 
              marginTop: '8px',
              fontWeight: '600'
            }}>
              You'll be billed ${((accountTypes.find(t => t.key === selectedType)?.price + (FEATURE_FLAGS.PHONE_FEATURE_ENABLED ? phoneSlots * getPhoneSlotPrice() : 0)) * 12 * (1 - ANNUAL_DISCOUNT)).toFixed(2)} annually
            </div>
          )}
        </div>
      )}

      {/* Billing Cycle Selection */}
      <div style={{
        ...styles.formSection,
        background: 'linear-gradient(135deg, #f1f5f9 0%, #e2e8f0 100%)',
        border: '1px solid #e2e8f0'
      }}>
        <h3 style={styles.sectionTitle}>💰 Billing Cycle</h3>
        <p style={styles.sectionDescription}>
          Choose your billing frequency. Save 20% with annual billing!
        </p>
        
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '8px'
        }}>
          <label style={{
            display: 'flex',
            alignItems: 'center',
            cursor: 'pointer',
            padding: '4px 16px', // Reduced vertical padding from 12px to 8px
            border: billingCycle === 'monthly' ? '2px solid #10b981' : '1px solid #e2e8f0',
            borderRadius: '8px',
            background: billingCycle === 'monthly' ? '#f0fdf4' : 'white',
            transition: 'all 0.2s ease',
            boxShadow: billingCycle === 'monthly' ? '0 2px 4px rgba(16, 185, 129, 0.1)' : 'none'
          }}>
            <input
              type="radio"
              name="billingCycle"
              value="monthly"
              checked={billingCycle === 'monthly'}
              onChange={(e) => setBillingCycle(e.target.value)}
              style={{
                marginRight: '12px',
                transform: 'scale(1.2)',
                accentColor: '#10b981'
              }}
            />
            <div style={{ flex: 1 }}>
              <span style={{
                fontSize: '14px',
                fontWeight: '600',
                color: billingCycle === 'monthly' ? '#059669' : '#1e293b'
              }}>Monthly Billing:</span>
              <span style={{
                fontSize: '13px',
                color: '#64748b',
                marginTop: '2px'
              }}> Pay monthly, cancel anytime</span>
            </div>
          </label>
          
          <label style={{
            display: 'flex',
            alignItems: 'center',
            cursor: 'pointer',
            padding: '4px 16px', // Reduced vertical padding from 12px to 8px
            border: billingCycle === 'annual' ? '2px solid #10b981' : '1px solid #e2e8f0',
            borderRadius: '8px',
            background: billingCycle === 'annual' ? '#f0fdf4' : 'white',
            transition: 'all 0.2s ease',
            position: 'relative',
            boxShadow: billingCycle === 'annual' ? '0 2px 4px rgba(16, 185, 129, 0.1)' : 'none'
          }}>
            <input
              type="radio"
              name="billingCycle"
              value="annual"
              checked={billingCycle === 'annual'}
              onChange={(e) => setBillingCycle(e.target.value)}
              style={{
                marginRight: '12px',
                transform: 'scale(1.2)',
                accentColor: '#10b981'
              }}
            />
            <div style={{ flex: 1 }}>
              <span style={{
                fontSize: '14px',
                fontWeight: '600',
                color: billingCycle === 'annual' ? '#059669' : '#1e293b'
              }}>Annual Billing:</span>
              <span style={{
                fontSize: '13px',
                color: '#64748b',
                marginTop: '2px'
              }}> Save 20% with yearly payment</span>
            </div>
            {/* Save Badge */}
            <div style={{
              background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
              color: 'white',
              padding: '4px 8px',
              borderRadius: '12px',
              fontSize: '14px', // Increased from '12px'
              fontWeight: '600'
            }}>
              SAVE 20%
            </div>
          </label>
        </div>
      </div>

      <div style={{
        ...styles.formSection,
        background: 'linear-gradient(135deg, #f1f5f9 0%, #e2e8f0 100%)',
        border: '1px solid #e2e8f0'
      }}>
        <h3 style={styles.sectionTitle}>💳 Payment Information</h3>
        <p style={styles.sectionDescription}>
          Safe & Encrypted Payments with Stripe. <img
            src="https://stripe.com/img/v3/home/twitter.png"
            alt="Stripe"
            style={{ height: "18px" }}
          /> 
        </p>
        
        <div style={{ position: 'relative' }}>
          <input
            style={{
              ...styles.input,
              borderColor: payment.name ? (validateName(payment.name) ? '#10b981' : '#ef4444') : '#e2e8f0',
              paddingRight: payment.name ? '40px' : '12px'
            }}
            type="text"
            placeholder="Cardholder name"
            name="name"
            value={payment.name}
            onChange={handleCardChange}
          />
          {payment.name && (
            <div style={{
              position: 'absolute',
              right: '12px',
              top: '38%',
              transform: 'translateY(-50%)',
              fontSize: '18px'
            }}>
              {validateName(payment.name) ? '✅' : '❌'}
            </div>
          )}
        </div>
        
        <div style={{ 
          ...styles.input,
          padding: '18px 18px',
          borderColor: cardError ? '#ef4444' : (cardComplete ? '#10b981' : '#e2e8f0'),
          borderWidth: '2px',
          minHeight: '48px',
          marginBottom: '1px'
        }}>
          <CardElement
            options={{
              style: {
                base: {
                  fontSize: '16px',
                  color: '#424770',
                  '::placeholder': {
                    color: '#aab7c4',
                  },
                  iconColor: '#666',
                },
                invalid: {
                  color: '#9e2146',
                },
              },
              hidePostalCode: false, // Include postal code for enhanced security
            }}
            onChange={handleCardElementChange}
          />
        </div>
        
        {cardError && (
          <div style={{ color: '#ef4444', marginTop: '8px', fontSize: '14px' }}>
            {cardError}
          </div>
        )}
      </div>
      {paymentError && <div style={{ color: 'red', marginTop: 8 }}>{paymentError}</div>}
      
      {/* Terms and Privacy Confirmation */}
      <div style={{
        borderRadius: 12,
        padding: 10,
        background: 'linear-gradient(135deg, #f1f5f9 0%, #e2e8f0 100%)',
        border: '2px solid #e5e7eb',
        marginBottom: 1,
        boxShadow: '0 4px 16px rgba(0,0,0,0.06)'
      }}>
        <label style={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: '12px',
          cursor: 'pointer',
          fontSize: 14, // Increased from 14
          lineHeight: '1.5',
          color: '#374151'
        }}>
          <input
            type="checkbox"
            checked={agreedToTerms}
            onChange={(e) => setAgreedToTerms(e.target.checked)}
            style={{
              width: '20px',
              height: '20px',
              marginTop: '2px',
              accentColor: '#10b981',
              cursor: 'pointer'
            }}
          />
          <span>
            I confirm the information is correct and agree to SmartClose’s{' '}
            <a
              href="/privacy"
              target="_blank"
              rel="noopener noreferrer"
              style={{
                color: '#3b82f6',
                textDecoration: 'none',
                fontWeight: 600,
                borderBottom: '1px dotted #3b82f6'
              }}
            >
              Terms of Service
            </a>
            {/* {' '}
            and{' '}
            <a
              href="/privacy"
              target="_blank"
              rel="noopener noreferrer"
              style={{
                color: '#3b82f6',
                textDecoration: 'none',
                fontWeight: 600,
                borderBottom: '1px dotted #3b82f6'
              }}
            >
              Privacy Policy
            </a> */}
            .
          </span>
        </label>
      </div>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 8 }}>
        <Button3D
          style={{ width: 140, fontSize: 16 }}
          onClick={() => updateStep(3)}
        >
          Back
        </Button3D>
        <div style={{ flex: 1 }} />
        <Button3D
          style={{ 
            width: 200, 
            fontSize: 16,
            fontWeight: 700,
            padding: '12px 24px',
            background: isPaymentValid() && !signupLoading
              ? 'linear-gradient(135deg, #10b981 0%, #059669 100%)'
              : 'linear-gradient(135deg, #9ca3af 0%, #6b7280 100%)',
            boxShadow: isPaymentValid() && !signupLoading
              ? '0 8px 25px rgba(16, 185, 129, 0.4)'
              : '0 4px 12px rgba(156, 163, 175, 0.2)',
            opacity: !isPaymentValid() || signupLoading ? 0.6 : 1,
            cursor: !isPaymentValid() || signupLoading ? 'not-allowed' : 'pointer',
            transition: 'all 0.3s ease'
          }}
          disabled={!isPaymentValid() || signupLoading}
          onClick={async () => {
            setSignupError("");
            setPaymentError("");
            
            // Validate payment information
            if (!validateName(payment.name)) {
              setPaymentError('Please enter the name on the card.');
              return;
            }
            if (!cardComplete) {
              setPaymentError('Please enter complete card information.');
              return;
            }
            if (cardError) {
              setPaymentError(cardError);
              return;
            }
            if (!stripe || !elements) {
              setPaymentError('Stripe is not loaded. Please refresh the page.');
              return;
            }

            setSignupLoading(true);
            try {
              let paymentMethodId = null;
              
              // Create payment method with Stripe Elements for paid plans
              if (selectedType !== 'free') {
                try {
                  const cardElement = elements.getElement(CardElement);
                  
                  const billingDetails = {
                    name: payment.name,
                    email: userDetails.email,
                  };
                  
                  console.log('Creating payment method with Stripe Elements...');
                  
                  // Use Stripe Elements service for secure tokenization (pass the stripe instance)
                  const result = await createAndSubmitPaymentMethodFromElements(stripe, cardElement, billingDetails);
                  
                  console.log('Payment method created successfully:', result.payment_method_id);
                  paymentMethodId = result.payment_method_id;
                } catch (error) {
                  console.error('Payment method creation failed:', error);
                  console.error('Error response:', error.response?.data);
                  console.error('Error status:', error.response?.status);
                  
                  // Handle axios error response
                  let errorMessage = 'Failed to process payment method. Please check your card details.';
                  if (error.response && error.response.data) {
                    errorMessage = error.response.data.error || errorMessage;
                  } else if (error.message) {
                    errorMessage = error.message;
                  }
                  
                  setPaymentError(errorMessage);
                  setSignupLoading(false);
                  return;
                }
              }

              // Phase 1: Call signup API to create user account
              console.log('🚀 Starting Phase 1: User account creation');
              console.log('🚀 Trial mode:', isTrialMode);
              console.log('🚀 Selected type:', selectedType);
              console.log('🚀 Account type being sent:', isTrialMode ? 'trial' : selectedType);
              
              const result = await signup({
                name: userDetails.name,
                last_name: userDetails.last_name,
                email: userDetails.email,
                password: userDetails.password,
                company: userDetails.company || '',
                accountType: isTrialMode ? 'trial' : selectedType,
                phoneSlots: phoneSlots, // include phone lines in signup
                billingCycle: billingCycle, // include billing cycle preference
                paymentMethodId: paymentMethodId, // include payment method for billing after trial
                isTrialMode: isTrialMode, // include trial mode flag
                trialDurationDays: isTrialMode ? TRIAL_CONFIG.DURATION_DAYS : undefined
              });
              
              console.log('🔍 Signup result received:', result);
              console.log('🔍 Signup success:', result?.success);
              console.log('🔍 Signup message:', result?.message);
              console.log('🔍 Signup user:', result?.user);
              
              if (result && result.success) {
                console.log('✅ Phase 1 complete: User account created successfully');
                
                // Phase 1.5: Login user to get authentication token for Phase 2
                console.log('🔐 Logging in user for authentication...');
                const loginResult = await login({
                  email: userDetails.email,
                  password: userDetails.password
                });
                
                console.log('🔍 Login result:', loginResult);
                console.log('🔍 Login success:', loginResult?.success);
                console.log('🔍 Login token:', loginResult?.token ? 'TOKEN_PRESENT' : 'NO_TOKEN');
                console.log('🔍 Login user:', loginResult?.user ? 'USER_PRESENT' : 'NO_USER');
                
                if (loginResult && loginResult.success) {
                  console.log('✅ User authenticated successfully');
                  // Store user and token for authenticated API calls
                  setCurrentUser(loginResult.user, loginResult.token);
                  
                  // Verify token was stored
                  const storedToken = localStorage.getItem('token');
                  console.log('🔍 Token stored in localStorage:', storedToken ? 'TOKEN_STORED' : 'NO_TOKEN_STORED');
                  
                  // Phase 2: Create subscription if payment method was provided and not in trial mode
                  if (paymentMethodId && selectedType !== 'Solo' && !isTrialMode) {
                    console.log('💳 Phase 2: Creating subscription...');
                    
                    const subscriptionResult = await createSubscription({
                      user_id: result.user.user_id,
                      accountType: selectedType,
                      billingCycle: billingCycle,
                      paymentMethodId: paymentMethodId
                    });
                    
                    console.log('🔍 Full subscription result:', subscriptionResult);
                    
                    if (subscriptionResult && subscriptionResult.success) {
                      console.log('✅ Phase 2 complete: Subscription created successfully');
                      console.log('✅ Subscription details:', subscriptionResult.data);
                      
                      // CRITICAL: Refresh user profile to update trial status
                      console.log('🔄 Refreshing user profile after subscription creation...');
                      // Note: We can't call refreshUserProfile here since we're not in AuthContext yet
                      // The user will be properly authenticated in confirmation step
                      // Just update localStorage with trial status cleared
                      const userData = JSON.parse(localStorage.getItem('user') || '{}');
                      if (userData) {
                        userData.is_trial = 'false';
                        userData.trial_status = 'completed';
                        localStorage.setItem('user', JSON.stringify(userData));
                        console.log('✅ Updated user trial status in localStorage');
                      }
                    } else {
                      console.warn('⚠️ Phase 2 warning: Subscription creation failed, but user account exists');
                      console.warn('Subscription error details:', {
                        success: subscriptionResult?.success,
                        message: subscriptionResult?.message,
                        error: subscriptionResult?.error,
                        fullResult: subscriptionResult
                      });
                      // Don't fail signup if subscription fails - user can set it up later
                    }
                  } else {
                    const skipReason = isTrialMode 
                      ? 'Trial mode - subscription will be created after trial period'
                      : 'No payment method or Solo plan (trial period)';
                    console.log(`⏭️ Phase 2 skipped: ${skipReason}`);
                  }
                } else {
                  console.warn('⚠️ Authentication failed after signup, skipping subscription creation');
                  console.warn('Login error:', loginResult?.message);
                  console.warn('Login result details:', loginResult);
                  // Don't fail signup if login fails - user can login manually later
                }
                
                setSignupLoading(false);
                
                // Show confirmation step instead of immediately navigating
                setShowConfirmation(true);
                sessionStorage.setItem('signup_showConfirmation', 'true');
                sessionStorage.setItem('signup_selectedType', JSON.stringify(selectedType));
                sessionStorage.setItem('signup_userDetails', JSON.stringify(userDetails));
                sessionStorage.setItem('signup_phoneSlots', JSON.stringify(phoneSlots));
                sessionStorage.setItem('signup_billingCycle', JSON.stringify(billingCycle));
                
                // Store auth info for later use in confirmation step - don't login yet
                const token = localStorage.getItem('token');
                const user = localStorage.getItem('user');
                
                if (token && user) {
                  console.log('✅ Authentication credentials stored, will login on confirmation button click');
                  // Store credentials in sessionStorage for confirmation step
                  sessionStorage.setItem('signup_auth_token', token);
                  sessionStorage.setItem('signup_auth_user', user);
                } else {
                  console.warn('⚠️ No authentication credentials found');
                }
              } else {
                setSignupLoading(false);
                setSignupError((result && result.message) || 'Signup failed.');
                console.error('❌ Phase 1 failed: Signup error:', result);
              }
            } catch (err) {
              setSignupLoading(false);
              setSignupError('Unexpected error during signup.');
              console.error('Unexpected signup error:', err);
            }
          }}
        >
          {signupLoading ? 'Creating Account...' : 'Complete Signup'}
        </Button3D>
      </div>
      {signupError && <div style={{ color: 'red', marginTop: 8 }}>{signupError}</div>}
    </div>
    );
  };

  // Helper function to get plan details from either accountTypes or trial config
  const getSelectedPlanDetails = (planKey) => {
    // First check accountTypes for regular plans
    const accountType = accountTypes.find(t => t.key === planKey);
    if (accountType) {
      return accountType;
    }
    
    // If not found and trial feature is enabled, check for trial plan
    if (isTrialFeatureEnabled() && planKey === 'trial') {
      return {
        key: 'trial',
        label: TRIAL_CONFIG.PLAN_DETAILS.displayName,
        seats: TRIAL_CONFIG.PLAN_DETAILS.features.find(f => f.includes('seats'))?.match(/\d+/)?.[0] || '1',
        price: TRIAL_CONFIG.PLAN_DETAILS.price,
        isTrial: true
      };
    }
    
    return null;
  };

  // Step 5: Confirmation
  const renderConfirmationStep = () => {
    const savedPhoneSlots = JSON.parse(sessionStorage.getItem('signup_phoneSlots')) || 0;
    const selectedPlan = getSelectedPlanDetails(selectedType);
    const isTrialPlan = selectedPlan?.isTrial || false;
    
    return (
      <div style={styles.stepContainer}>
        {/* Success Icon and Title */}
        <div style={{ textAlign: 'center', marginBottom: 30 }}>
          <div style={{
            width: 50,
            height: 50,
            borderRadius: '50%',
            background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 8px',
            boxShadow: '0 12px 40px rgba(16, 185, 129, 0.3)',
            animation: 'successPulse 2s ease-in-out infinite',
            position: 'relative'
          }}>
            <span style={{ fontSize: 28, color: 'white', fontWeight: 'bold' }}>✓</span>
            <div style={{
              position: 'absolute',
              top: '-8px',
              right: '-8px',
              width: '32px',
              height: '32px',
              background: 'linear-gradient(135deg, #fbbf24 0%, #f59e0b 100%)',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: '0 4px 12px rgba(251, 191, 36, 0.4)'
            }}>
              <span style={{ fontSize: '18px' }}>🎉</span>
            </div>
          </div>
          <h2 style={{
            ...styles.title,
            background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
            fontSize: 25,
            marginBottom: 1,
            fontWeight: 600
          }}>
            Account Created Successfully!
          </h2>
        </div>

        {/* Comprehensive Account Summary */}
        <div style={{
          background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)',
          borderRadius: 20,
          padding: 15,
          border: '2px solid #e2e8f0',
          marginBottom: 18,
          boxShadow: '0 8px 25px rgba(0,0,0,0.08)',
          position: 'relative'
        }}>
          {/* Header with icon */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '12px',
            marginBottom: 8
          }}>
            <div style={{
              width: '28px',
              height: '28px',
              background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
              borderRadius: '12px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: '0 4px 12px rgba(59, 130, 246, 0.3)'
            }}>
              <span style={{ fontSize: '18px', color: 'white' }}>📋</span>
            </div>
            <h3 style={{
              fontSize: 18,
              fontWeight: 600,
              color: '#1e293b',
              margin: 0,
              letterSpacing: '-0.02em'
            }}>
              Account Summary
            </h3>
          </div>
          
          {/* Account Details Grid */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
            gap: 20,
            marginBottom: 8
          }}>
            {/* Plan Details Card */}
            <div style={{
              background: 'white',
              borderRadius: 16,
              padding: 10,
              border: '1px solid #e2e8f0',
              boxShadow: '0 2px 8px rgba(0,0,0,0.04)'
            }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                marginBottom: 8
              }}>
                <span style={{ fontSize: '18px' }}>💼</span>
                <h4 style={{
                  fontSize: 16,
                  fontWeight: 600,
                  color: '#374151',
                  margin: 0
                }}>
                  Subscription Plan
                </h4>
              </div>
              
              <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                marginBottom: 8
              }}>
                <span style={{ color: '#6b7280', fontSize: 14, fontWeight: 500 }}>Plan Type:</span>
                <span style={{
                  padding: '6px 14px',
                  background: isTrialPlan 
                    ? 'linear-gradient(135deg, #10b981 0%, #059669 100%)'
                    : 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
                  color: 'white',
                  borderRadius: 12,
                  fontSize: 13,
                  fontWeight: 600,
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px'
                }}>
                  {selectedPlan?.label || 'Unknown Plan'}
                </span>
              </div>
              
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                marginBottom: 8
              }}>
                <span style={{ color: '#6b7280', fontSize: 14, fontWeight: 500 }}>User Seats:</span>
                <span style={{ color: '#1f2937', fontWeight: 600, fontSize: 14 }}>
                  {selectedPlan?.seats || '0'} seat{selectedPlan?.seats !== '1' ? 's' : ''}
                </span>
              </div>
              
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                marginBottom: 8
              }}>
                <span style={{ color: '#6b7280', fontSize: 14, fontWeight: 500 }}>Price:</span>
                <span style={{ color: '#1f2937', fontWeight: 600, fontSize: 14 }}>
                  {isTrialPlan ? 'Free Trial' : `$${selectedPlan?.price || '0'}/month`}
                </span>
              </div>
              
              {FEATURE_FLAGS.PHONE_FEATURE_ENABLED && (
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  marginBottom: 8
                }}>
                  <span style={{ color: '#6b7280', fontSize: 14, fontWeight: 500 }}>Phone Slots:</span>
                  <span style={{ color: '#1f2937', fontWeight: 600, fontSize: 14 }}>
                    {savedPhoneSlots || 0} slot{(savedPhoneSlots || 0) !== 1 ? 's' : ''}
                  </span>
                </div>
              )}
              
              {isTrialPlan && (
                <div style={{
                  marginTop: 8,
                  padding: 10,
                  background: 'linear-gradient(135deg, #d1fae5 0%, #a7f3d0 100%)',
                  borderRadius: 10,
                  border: '1px solid #6ee7b7'
                }}>
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    marginBottom: 4
                  }}>
                    <span style={{ fontSize: '14px' }}>🎁</span>
                    <span style={{
                      color: '#047857',
                      fontSize: 15,
                      fontWeight: 600,
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px'
                    }}>
                      Trial Benefits
                    </span>
                  </div>
                  <p style={{
                    color: '#065f46',
                    fontSize: 13,
                    margin: 0,
                    lineHeight: '1.3'
                  }}>
                    14-day free trial • No credit card required
                  </p>
                </div>
              )}
            </div>

            {/* Account Details Card */}
            <div style={{
              background: 'white',
              borderRadius: 16,
              padding: 10,
              border: '1px solid #e2e8f0',
              boxShadow: '0 2px 8px rgba(0,0,0,0.04)'
            }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                marginBottom: 8
              }}>
                <span style={{ fontSize: '18px' }}>👤</span>
                <h4 style={{
                  fontSize: 16,
                  fontWeight: 600,
                  color: '#374151',
                  margin: 0
                }}>
                  Account Details
                </h4>
              </div>
              
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                gap: 8,
                marginBottom: 8,
              }}>
                <span style={{ color: '#6b7280', fontSize: 14, fontWeight: 500, flexShrink: 0 }}>Name:</span>
                <span style={{
                  color: '#1f2937',
                  fontWeight: 600,
                  fontSize: 14,
                  textAlign: 'right',
                  flex: 1,
                  minWidth: 0,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}>
                  {userDetails.name} {userDetails.last_name}
                </span>
              </div>

              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                gap: 8,
                marginBottom: 8,
              }}>
                <span style={{ color: '#6b7280', fontSize: 14, fontWeight: 500, flexShrink: 0 }}>Email:</span>
                <span style={{
                  color: '#1f2937',
                  fontWeight: 600,
                  fontSize: 14,
                  textAlign: 'right',
                  flex: 1,
                  minWidth: 0,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}>
                  {userDetails.email}
                </span>
              </div>
              
              {FEATURE_FLAGS.PHONE_FEATURE_ENABLED && savedPhoneSlots > 0 && (
                <div style={{
                  marginTop: 12,
                  padding: 10,
                  background: 'linear-gradient(135deg, #ddd6fe 0%, #c4b5fd 100%)',
                  borderRadius: 10,
                  border: '1px solid #a78bfa'
                }}>
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                  }}>
                    <span style={{
                      color: '#5b21b6',
                      fontSize: 13,
                      fontWeight: 600,
                      display: 'flex',
                      alignItems: 'center',
                      gap: '4px'
                    }}>
                      📞 Phone Add-on
                    </span>
                    <span style={{
                      color: '#581c87',
                      fontWeight: 700,
                      fontSize: 13
                    }}>
                      {savedPhoneSlots} slot{savedPhoneSlots !== 1 ? 's' : ''}
                    </span>
                  </div>
                </div>
              )}
            </div>
          </div>
        
      </div>

        {/* Action Button */}
        <div style={{ textAlign: 'center' }}>
          <Button3D
            disabled={!agreedToTerms}
            style={{ 
              width: 260, 
              fontSize: 16, 
              display: 'inline-block',
              background: agreedToTerms 
                ? 'linear-gradient(135deg, #10b981 0%, #059669 100%)'
                : 'linear-gradient(135deg, #9ca3af 0%, #6b7280 100%)',
              boxShadow: agreedToTerms 
                ? '0 10px 30px rgba(16, 185, 129, 0.4)'
                : '0 4px 12px rgba(156, 163, 175, 0.2)',
              border: 'none',
              color: 'white',
              cursor: agreedToTerms ? 'pointer' : 'not-allowed',
              opacity: agreedToTerms ? 1 : 0.6,
              transition: 'all 0.3s ease',
              padding: '16px 32px',
              fontWeight: 700,
              textTransform: 'uppercase',
              letterSpacing: '0.5px'
            }}
            onClick={async () => {
              if (!agreedToTerms) return;
              // Clear signup-specific session data
              sessionStorage.removeItem('signup_showConfirmation');
              sessionStorage.removeItem('signup_selectedType');
              sessionStorage.removeItem('signup_userDetails');
              sessionStorage.removeItem('signup_phoneSlots');
              sessionStorage.removeItem('signup_billingCycle');
              
              // Reset component state
              setShowConfirmation(false);
              setStep(1);
              setSelectedType(null);
              setUserDetails({ email: '', password: '', name: '' });
              setPayment({ name: '' });
              setCardComplete(false);
              setCardError(null);
              setPhoneSlots(0);
              setSkipPhoneAddon(false);
              setSignupError("");
              setSignupLoading(false);
              setAgreedToTerms(false);
              
              // Check for stored credentials from signup process first
              let token = sessionStorage.getItem('signup_auth_token');
              let user = sessionStorage.getItem('signup_auth_user');
              
              // If no stored credentials, check localStorage
              if (!token || !user) {
                token = localStorage.getItem('token');
                user = localStorage.getItem('user');
              }
              
              if (token && user) {
                try {
                  // Update auth context with existing credentials
                  authLogin(JSON.parse(user), token);
                  console.log('✅ Authentication state updated, navigating to calendar connection');
                  
                  // Clean up stored auth credentials
                  sessionStorage.removeItem('signup_auth_token');
                  sessionStorage.removeItem('signup_auth_user');
                  
                  navigate('/register');
                } catch (error) {
                  console.error('❌ Error updating auth state:', error);
                  // If auth update fails, still navigate but user might need to login again
                  navigate('/register');
                }
              } else {
                console.warn('⚠️ No authentication credentials found, navigating to login');
                navigate('/', { state: { openLogin: true } });
              }
            }}
          >
            🚀 Start My Journey
          </Button3D>
        </div>

        {/* CSS Animation */}
        <style>
          {`
            @keyframes successPulse {
              0%, 100% { transform: scale(1); }
              50% { transform: scale(1.05); }
            }
            
            .trial-badge {
              background: linear-gradient(135deg, #6366f1 0%, #4f46e5 100%);
              color: white;
              font-size: 9px;
              font-weight: 700;
              padding: 2px 6px;
              border-radius: 6px;
              letter-spacing: 0.5px;
              box-shadow: 0 1px 3px rgba(99, 102, 241, 0.3);
            }
            
            .trial-indicator {
              position: absolute;
              top: 8px;
              right: 8px;
              background: linear-gradient(135deg, #6366f1 0%, #4f46e5 100%);
              border-radius: 50%;
              width: 20px;
              height: 20px;
              display: flex;
              align-items: center;
              justify-content: center;
              box-shadow: 0 2px 4px rgba(99, 102, 241, 0.3);
            }
            
            .trial-indicator-icon {
              color: white;
              font-size: 12px;
              font-weight: bold;
            }
          `}
        </style>
      </div>
    );
  };

  return (
    <>
      {console.log('🎯 Rendering Signup, configLoading:', configLoading, 'step:', step)}
      {configLoading ? (
        <div style={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center', 
          height: '100vh',
          flexDirection: 'column',
          gap: '20px'
        }}>
          <Spinner animation="border" role="status" />
          <div>Loading configuration...</div>
        </div>
      ) : (
        <>
          <NavBar onSignInClick={handleSignInClick} onProfileClick={onProfileClick} onBillingClick={onBillingClick} onSeatsUpdate={onSeatsUpdate} />
      <header className="App-header" style={{
        background: 'linear-gradient(180deg, #eef2f0 0%, #e4ece8 100%)',
        borderBottom: '1px solid #c8d8d2',
        boxShadow: '0 1px 3px rgba(15, 23, 42, 0.07)',
        borderRadius: 0,
        margin: 0,
        padding: '0 20px',
        minHeight: 42,
        flexShrink: 0,
      }}>
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', width: '100%', height: '100%' }}>
          <h2 style={{
            margin: 0, color: '#1e3a2f',
            fontSize: 18, fontWeight: 700,
            letterSpacing: '-0.01em',
          }}>Sign Up for SmartClose</h2>
        </div>
      </header>
      <div style={styles.signupContainer}>
        <>
          {showConfirmation ? renderConfirmationStep() : (
            <>
              {step === 1 && renderAccountTypeStep()}
              {step === 2 && FEATURE_FLAGS.PHONE_FEATURE_ENABLED && renderPhoneAddonStep()}
              {step === 3 && renderUserDetailsStep()}
              {step === 4 && renderPaymentStep()}
            </>
          )}
        </>
      </div>
        </>
      )}
    </>
  );
}

export default Signup;
